import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Typography, CircularProgress } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import NodeAutocomplete from './NodeAutocomplete';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeTableSelector — Autocomplete for database tables + fetch columns button + open link.
 * Used by: Create DB, Update DB, Query DB, Trigger on DB Update.
 */

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
});

const ActionsRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

const SmallButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: NODE_UI.fontSize.caption,
  fontWeight: NODE_UI.fontWeight.label,
  padding: '2px 8px',
  minHeight: 24,
  borderRadius: NODE_UI.borderRadiusSm,
  color: theme.palette.text.secondary,
  borderColor: theme.palette.divider,
  '&:hover': {
    borderColor: theme.palette.text.primary,
    color: theme.palette.text.primary,
  },
}));

const NodeTableSelector = React.memo(function NodeTableSelector({
  label = 'Select Table',
  value,
  onChange,
  tables = [],
  onFetchColumns,
  onOpenTable,
  loading = false,
  columnsLoading = false,
  placeholder = 'Search tables...',
  getOptionLabel,
  isOptionEqualToValue,
  sx,
}) {
  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      <NodeAutocomplete
        label={label}
        value={value}
        onChange={onChange}
        options={tables}
        getOptionLabel={getOptionLabel || ((opt) => opt?.name || opt?.label || '')}
        isOptionEqualToValue={isOptionEqualToValue || ((opt, val) => opt?.id === val?.id)}
        placeholder={placeholder}
        loading={loading}
      />
      {value && (
        <ActionsRow>
          {onFetchColumns && (
            <SmallButton
              variant="outlined"
              size="small"
              onClick={(e) => { stopPropagation(e); onFetchColumns(); }}
              disabled={columnsLoading}
              startIcon={columnsLoading ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
            >
              {columnsLoading ? 'Loading...' : 'Fetch Columns'}
            </SmallButton>
          )}
          {onOpenTable && (
            <SmallButton
              variant="outlined"
              size="small"
              onClick={(e) => { stopPropagation(e); onOpenTable(); }}
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            >
              Open Table
            </SmallButton>
          )}
        </ActionsRow>
      )}
    </Root>
  );
});

export default NodeTableSelector;
